import config from '../../config';

import shiftSwapDetailsUrl from './shift-swap-details.html';

class ShiftSwapDetailsCtrl {
  constructor(
    Authentication,
    EventCollection,
    ShiftUtils,
    toDateLongFilter,
    extendShiftModel,
    sdk
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      EventCollection,
      ShiftUtils,
      toDateLongFilter,
      extendShiftModel,
      sdk,
    });

    this.buttonTemplates = {
      CANCEL: {
        text: 'Cancel swap',
        click: () => this.cancelSwap(this.swapDetails.id),
        type: 'btn-lg btn-danger-inversed',
      },
      REJECT: {
        text: 'Reject offer',
        click: () => this.rejectSwap(this.swapDetails.id),
        type: 'btn-lg btn-danger-inversed',
      },
      ACCEPT: {
        text: 'Accept offer',
        click: () => this.acceptSwap(this.swapDetails.id),
        type: 'btn-lg btn-success-inversed',
      },
      DENY: {
        text: 'Deny swap',
        click: () => this.denySwap(this.swapDetails.id),
        type: 'btn-lg btn-danger-inversed',
      },
      APPROVE: {
        text: 'Approve swap',
        click: () => this.approveSwap(this.swapDetails.id),
        type: 'btn-lg btn-success-inversed',
      },
      RETRACT: {
        text: 'Retract acceptance',
        click: () => this.retractAcceptance(this.swapDetails.id),
        type: 'btn-lg btn-danger-inversed',
      },
    };
  }

  $onInit() {
    this.loadSwapDetails(this.shift.exchangeData.swap.id);
  }

  async loadSwapDetails(swapId) {
    const swapDetails = await this.EventCollection.loadSwapDetails(swapId);
    swapDetails.offererShift = this.extendShiftModel(swapDetails.offererShift);
    swapDetails.offereeShift = this.extendShiftModel(swapDetails.offereeShift);

    this.offererShiftDescription = `${this.toDateLongFilter(
      swapDetails.offererShift.dtstart,
      'EEE'
    )} • ${swapDetails.offererShift.displayLabel}`;
    this.offereeShiftDescription = `${this.toDateLongFilter(
      swapDetails.offereeShift.dtstart,
      'EEE'
    )} • ${swapDetails.offereeShift.displayLabel}`;

    this.swapDetails = swapDetails;
    this.refreshButtons();
  }

  get originalShiftDescription() {
    const { isOfferedByMe } = this.shift.exchangeData;
    return this.sdk('descriptionHelpers').getOriginalShiftDescription(isOfferedByMe, this.offerer);
  }

  get goalShiftDescription() {
    const { isOfferedToMe } = this.shift.exchangeData;
    return this.sdk('descriptionHelpers').getGoalShiftDescription(isOfferedToMe, this.offeree);
  }

  get shouldHideHourDifference() {
    return Boolean(
      this.swapDetails?.offererShift?.openEnd || this.swapDetails?.offereeShift?.openEnd
    );
  }

  get swapStatus() {
    return this.sdk('descriptionHelpers').getSwapStatusDescription(
      this.shift.exchangeData,
      this.offeree,
      this.Authentication.user.orgSettings.shiftExchangeRequiresApproval
    );
  }

  refreshButtons() {
    this.swapButtons = [];

    const { amIOfferer, amIOfferee, swap } = this.shift.exchangeData;
    const { offereeShift, offererShift } = this.swapDetails;
    const { SWAP_PENDING_STATUS, SWAP_ACCEPTED_STATUS } = this.sdk('constants');
    const { canActOnLocation } = this.Authentication.user;

    if (amIOfferer) {
      this.swapButtons.push(this.buttonTemplates.CANCEL);
    }

    if (amIOfferee) {
      if (swap.status === SWAP_PENDING_STATUS) {
        this.swapButtons.push(this.buttonTemplates.REJECT);
        this.swapButtons.push(this.buttonTemplates.ACCEPT);
      } else {
        this.swapButtons.push(this.buttonTemplates.RETRACT);
      }
    }

    if (
      !this.swapButtons.length &&
      (canActOnLocation(offereeShift.location) || canActOnLocation(offererShift.location))
    ) {
      if (swap.status === SWAP_ACCEPTED_STATUS) {
        this.swapButtons.push(this.buttonTemplates.DENY);
        this.swapButtons.push(this.buttonTemplates.APPROVE);
      } else {
        this.swapButtons.push(this.buttonTemplates.CANCEL);
      }
    }
  }

  async cancelSwap(swapId) {
    try {
      await this.EventCollection.cancelShiftSwap(swapId);
    } catch {
      // do nothing
    }
    this.onSwapCancel();
  }

  async rejectSwap(swapId) {
    try {
      await this.EventCollection.cancelShiftSwap(swapId);
    } catch {
      // do nothing
    }
    this.onSwapReject();
  }

  async acceptSwap(swapId) {
    try {
      await this.ShiftUtils.performConflictingRequest({
        request: (ignoreConflicts) => {
          const params = ignoreConflicts ? { ignoreConflicts: true } : {};
          if (config.CLOPENING_CONFLICT_ENABLED) {
            params.checkRestBreakConflicts = true;
          }
          return this.EventCollection.approveShiftSwap(swapId, params);
        },
      });
      this.onSwapAccept();
    } catch {
      // do nothing
    }
  }

  async retractAcceptance(swapId) {
    try {
      await this.EventCollection.cancelShiftSwap(swapId);
    } catch {
      // do nothing
    }
    this.onSwapRetractAcceptance();
  }

  async denySwap(swapId) {
    try {
      await this.EventCollection.cancelShiftSwap(swapId);
    } catch {
      // do nothing
    }
    this.onSwapDeny();
  }

  async approveSwap(swapId) {
    try {
      await this.ShiftUtils.performConflictingRequest({
        request: (ignoreConflicts) => {
          const params = ignoreConflicts ? { ignoreConflicts: true } : {};
          if (config.CLOPENING_CONFLICT_ENABLED) {
            params.checkRestBreakConflicts = true;
          }

          return this.EventCollection.approveShiftSwap(swapId, params);
        },
      });
      this.onSwapApprove();
    } catch {
      // do nothing
    }
  }
}

export default {
  controller: ShiftSwapDetailsCtrl,
  controllerAs: 'ctrl',
  bindings: {
    shift: '<',
    offerer: '<',
    offeree: '<',
    onBack: '&',
    onSwapCancel: '&',
    onSwapReject: '&',
    onSwapAccept: '&',
    onSwapRetractAcceptance: '&',
    onSwapDeny: '&',
    onSwapApprove: '&',
  },
  templateUrl: shiftSwapDetailsUrl,
};
